import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { Subscription } from 'rxjs';

import { ComissaoEstatisticaService } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.service';
import { ComissaoConselheiroAtuacaoResponse, ComissaoEstatisticaAgrupadaResponse, ComissaoEstatisticaReuniaoFiltroRequest } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { truncarRotuloCategoria, tituloTooltipCompleto } from '../../grafico-rotulos';

@Component({
    selector: 'app-conselheiros-estatisticas',
    standalone: true,
    imports: [CommonModule, ChartModule, TableModule, CardContent],
    templateUrl: './conselheiros-estatisticas.html',
    styleUrls: ['./conselheiros-estatisticas.scss']
})
export class ConselheirosEstatisticasComponent implements OnChanges, OnDestroy {
    private readonly estatisticaService = inject(ComissaoEstatisticaService);

    @Input() dataInicio = '';
    @Input() dataFim = '';
    @Input() uf: string | null = null;

    variacaoMinutos = 0;
    variacaoTotalReunioes = 0;
    carregandoVariacao = false;

    graficoAtuantes: { labels: string[]; datasets: unknown[] } = { labels: [], datasets: [] };
    graficoFaltas: { labels: string[]; datasets: unknown[] } = { labels: [], datasets: [] };
    readonly opcoesGrafico = {
        indexAxis: 'y',
        plugins: {
            legend: { display: false },
            // O eixo pode estar truncado: o tooltip mostra o nome completo.
            tooltip: { callbacks: { title: tituloTooltipCompleto } }
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                ticks: {
                    autoSkip: false,
                    // Sem isto o Chart.js corta o rotulo pela esquerda e come o inicio do nome.
                    callback: truncarRotuloCategoria()
                }
            }
        }
    };

    linhas: ComissaoConselheiroAtuacaoResponse[] = [];
    totalRegistros = 0;
    rows = 20;
    first = 0;
    sortField = 'totalPresencas';
    sortOrder = -1;
    carregandoTabela = false;

    private subs: Subscription[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['dataInicio'] || changes['dataFim'] || changes['uf']) {
            this.carregarVariacao();
            this.carregarGraficos();
            this.first = 0;
            this.carregarTabela();
        }
    }

    ngOnDestroy(): void {
        this.subs.forEach((s) => s.unsubscribe());
    }

    /**
     * A média chega em minutos, mas na prática dá dias: "+12140 min" é ilegível.
     * Escala a unidade conforme a grandeza — minutos, horas ou dias.
     */
    get variacaoFormatada(): string {
        const minutos = this.variacaoMinutos;
        const sinal = minutos > 0 ? '+' : minutos < 0 ? '−' : '';
        const absoluto = Math.abs(minutos);

        if (absoluto < 60) {
            return `${sinal}${absoluto} min`;
        }
        const formatar = (valor: number, casas: number) => valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

        if (absoluto < 60 * 24) {
            const horas = absoluto / 60;
            return `${sinal}${formatar(horas, horas < 10 ? 1 : 0)} h`;
        }
        const dias = absoluto / (60 * 24);
        return `${sinal}${formatar(dias, dias < 10 ? 1 : 0)} ${dias >= 2 ? 'dias' : 'dia'}`;
    }

    private filtro(): ComissaoEstatisticaReuniaoFiltroRequest {
        return {
            tipoPeriodo: 'POR_PERIODO',
            dataInicioReuniao: this.dataInicio || null,
            dataFimReuniao: this.dataFim || null,
            ufSgUf: this.uf || undefined,
            excluirStatusReuniaoExcluida: false
        } as ComissaoEstatisticaReuniaoFiltroRequest;
    }

    private carregarVariacao(): void {
        this.carregandoVariacao = true;
        this.subs.push(
            this.estatisticaService.consultarVariacaoMediaReuniao(this.filtro()).subscribe({
                next: (resp) => {
                    this.variacaoMinutos = resp?.mediaMinutos ?? 0;
                    this.variacaoTotalReunioes = resp?.totalReunioes ?? 0;
                    this.carregandoVariacao = false;
                },
                error: () => {
                    this.variacaoMinutos = 0;
                    this.variacaoTotalReunioes = 0;
                    this.carregandoVariacao = false;
                }
            })
        );
    }

    private carregarGraficos(): void {
        this.subs.push(
            this.estatisticaService.listarConselheirosAtuantes(this.filtro()).subscribe({
                next: (dados) => (this.graficoAtuantes = this.montarGrafico(dados, this.corPrimaria())),
                error: () => (this.graficoAtuantes = { labels: [], datasets: [] })
            })
        );
        this.subs.push(
            this.estatisticaService.listarConselheirosFaltas(this.filtro()).subscribe({
                next: (dados) => (this.graficoFaltas = this.montarGrafico(dados, '#ef4444')),
                error: () => (this.graficoFaltas = { labels: [], datasets: [] })
            })
        );
    }

    private corPrimaria(): string {
        return getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || '#3b82f6';
    }

    private montarGrafico(dados: ComissaoEstatisticaAgrupadaResponse[], cor: string): { labels: string[]; datasets: unknown[] } {
        const itens = dados ?? [];
        return {
            labels: itens.map((item) => item.descricao ?? item.codigo ?? ''),
            datasets: [{ data: itens.map((item) => item.total ?? 0), backgroundColor: cor }]
        };
    }

    carregarTabela(evento?: TableLazyLoadEvent): void {
        if (evento) {
            this.first = evento.first ?? 0;
            this.rows = evento.rows ?? this.rows;
            const campo = Array.isArray(evento.sortField) ? evento.sortField[0] : evento.sortField;
            if (campo) {
                this.sortField = campo;
                this.sortOrder = evento.sortOrder ?? -1;
            }
        }
        const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
        const sort = `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`;
        this.carregandoTabela = true;
        this.subs.push(
            this.estatisticaService.consultarAtuacaoConselheiros(this.filtro(), page, this.rows, sort).subscribe({
                next: (resp) => {
                    this.linhas = resp?.content ?? [];
                    this.totalRegistros = resp?.totalElements ?? 0;
                    this.carregandoTabela = false;
                },
                error: () => {
                    this.linhas = [];
                    this.totalRegistros = 0;
                    this.carregandoTabela = false;
                }
            })
        );
    }
}
